import { useCallback } from 'react';

import { useAuth } from '@/hooks/use-auth';

const ENDPOINT = '/api/software-download';

/** The installer's file name is the only version marker it has (`NoPingSetupV4.exe`, `NoPingBetav4.1.34.37.exe`). */
export function installerVersionFrom(installerUrl: string): string | null {
  try {
    const fileName = new URL(installerUrl, 'https://noping.com').pathname.split('/').pop();
    return fileName || null;
  } catch {
    return null;
  }
}

/**
 * Returns the click handler every installer download button calls (SCRUM-2097).
 *
 * `sourcePage` names the button, not just the route — `/download` alone has three of them.
 *
 * The click usually leaves the page (tutorial navigation, `window.location` to the `.exe`), which is
 * why this is a beacon: the browser delivers it after the document is gone. `fetch` with `keepalive`
 * is the fallback for browsers without `sendBeacon`. Nothing here may delay or block the download,
 * so it never throws and never awaits.
 */
export function useTrackSoftwareDownload(sourcePage: string) {
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useCallback(
    (installerUrl: string) => {
      if (typeof window === 'undefined') return;

      const body = JSON.stringify({ user_id: userId, source_page: sourcePage, installer_version: installerVersionFrom(installerUrl) });

      try {
        if (typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }))) return;
      } catch {
        // Some browsers throw on a Blob with a non-CORS-safelisted type; the fetch below covers them.
      }

      // The caller runs this inside the click that starts the download, before its own code: anything
      // thrown here would cancel the download itself, so a missing `fetch` is a silent no-op too.
      if (typeof fetch !== 'function') return;
      try {
        fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
      } catch {
        // Same reason as above: losing the row is acceptable, losing the download is not.
      }
    },
    [sourcePage, userId]
  );
}
